import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Modal, Skeleton, Switch } from 'antd';
import { isEmpty } from 'lodash';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import {
  useActivateOrInactivateArea,
  useArea,
  useDeleteArea,
  useUpdateArea,
} from '@/core/domain/operational/Areas/Areas.hooks';

import { PageProps } from '@/types/PageProps';

import AreasGeneralForm from '@/components/Operational/Areas/General/Form';
import SubareasModule from '@/components/Operational/Areas/SubareasModule';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import { CreateAreaRequest } from '@/core/domain/operational/Areas/Areas.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import AreaFooter from '@/components/Operational/Areas/AreaFooter';
import handleFormChange from '@/hooks/handleFormChange';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const AreaDetailsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<CreateAreaRequest>();
  const { setParamLabel } = useBreadcrumbs();
  const { areaId } = useRouteMatch<{ areaId: string }>().params;
  const [active, setActive] = useState<boolean>(false);
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const { data: area, isLoading: isLoadingArea } = useArea(+areaId);

  const activateOrInactivateArea = useActivateOrInactivateArea();

  const updateMutation = useUpdateArea();
  const deleteMutation = useDeleteArea();

  useEffect(() => {
    if (!isEmpty(area) && area) {
      form.setFieldsValue({
        name: area?.name,
        active: area?.active,
      });

      setActive(area?.active);
      setParamLabel(':areaId', area?.name || '');
    }
  }, [area]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      const obj: CreateAreaRequest = {
        ...values,
        id: Number(areaId),
        active: active,
      };

      await updateMutation.mutateAsync(obj);
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'areas.modal.remove.title' }),
      content: `${intl.formatMessage({
        id: 'areas.modal.remove.content',
      })} "${area?.name}"`,
      onOk: async () => {
        // Fetch Service to remove area item.
        await deleteMutation.mutateAsync(+areaId);

        history.push('/app/operational/areas');
      },
      okText: intl.formatMessage({
        id: 'areas.modal.remove.button.ok',
      }),
      okType: 'danger',
    });
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateArea.mutateAsync(Number(areaId));

      setActive(active);
    } catch {}
  };

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SpaceForButton>
          <Switch
            unCheckedChildren={intl.formatMessage({
              id: 'commons.entity.inactive',
            })}
            checkedChildren={intl.formatMessage({
              id: 'commons.entity.active',
            })}
            loading={isLoadingArea}
            checked={active}
            onChange={handleActiveClick}
            disabled={!hasPermissions([Authorities.REGISTER_AREA_EDIT])}
          />

          <Button
            block
            danger
            onClick={handleRemoveClick}
            loading={deleteMutation.isLoading}
            disabled={!hasPermissions([Authorities.REGISTER_AREA_DELETE])}
          >
            <FormattedMessage id="commons.form.button.remove" />
          </Button>

          <SaveButton
            block
            type="primary"
            onClick={handleSaveClick}
            loading={updateMutation.isLoading}
            disabled={!hasPermissions([Authorities.REGISTER_AREA_ADD])}
          >
            <FormattedMessage id="commons.form.button.save" />
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Skeleton loading={isLoadingArea}>
        <Form.Provider
          onFormChange={(name, info) =>
            handleFormChange(name, info, setFormChange)
          }
        >
          <AreasGeneralForm form={form} />
        </Form.Provider>

        <SubareasModule />

        <AreaFooter disabled={disabled} />
      </Skeleton>
    </TabContentWrapper>
  );
};

export default AreaDetailsPage;
